import React from "react";
import "./EsignStatusIcon.css";

// frontend/src/components/staff/EsignStatusIcon.js
//
// ESIGN-UX-REDESIGN-001 — Story 2 (status indicator), Surface 2: staff
// customer-list mini-icon (light-theme).
//
// Spec sources (locked):
//   docs/design/ESIGN-UX-REDESIGN-001-status-indicator-design.md (Zuri)
//     §4 — placement (32px leftmost column), 16pt icon, 4-state vocabulary
//     §4.5 — accessibility (aria-label per state, never color-only)
//   docs/architecture/ESIGN-UX-REDESIGN-001-tech.md (Marcus)
//     §Story 2 — list endpoint surfaces BE-derived `esignOverallStatus`
//
// 4 states + 4 icons + 4 colors. Each state has a distinct icon SHAPE so
// colorblind staff can read the state from shape alone (per Zuri §4.2 +
// WCAG color-not-only rule).
//
// Production-safety:
//   - Pure-presentational; renders an "—" placeholder (no label, no icon)
//     when `overallStatus` is missing or unknown so a missing field never
//     crashes the list row.

const TOOLTIPS = {
  signed: "ESIGN: signed",
  partial: "ESIGN: partial — one form pending",
  "not-signed": "ESIGN: not yet signed",
  revoked: "ESIGN: revoked",
};

const ICONS = {
  signed: (
    // check-circle filled
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <circle cx="8" cy="8" r="7" fill="currentColor" />
      <path
        d="M5 8l2.2 2.2L11 6"
        stroke="#ffffff"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ),
  partial: (
    // warn-triangle filled
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <path
        d="M8 1.5l7 13H1L8 1.5z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      <path
        d="M8 6v3.5"
        stroke="#ffffff"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="8" cy="11.5" r="0.8" fill="#ffffff" />
    </svg>
  ),
  "not-signed": (
    // x-circle outlined (muted)
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5.5 5.5l5 5M10.5 5.5l-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  revoked: (
    // x-circle filled
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <circle cx="8" cy="8" r="7" fill="currentColor" />
      <path
        d="M5.5 5.5l5 5M10.5 5.5l-5 5"
        stroke="#ffffff"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
};

/**
 * Staff customer-list ESIGN mini-icon. 16pt, leftmost column.
 *
 * @param {object} props
 * @param {'signed' | 'partial' | 'not-signed' | 'revoked' | null | undefined} props.overallStatus
 *   — the BE-derived `esignOverallStatus` field on a list-item payload.
 *   Missing / unknown values render as "—" (graceful fallback).
 */
const EsignStatusIcon = ({ overallStatus }) => {
  const known = ["signed", "partial", "not-signed", "revoked"].includes(
    overallStatus
  );
  if (!known) {
    return (
      <span
        className="esign-status-icon esign-status-icon--unknown"
        aria-label="ESIGN status unavailable"
        title="ESIGN status unavailable"
      >
        —
      </span>
    );
  }

  const label = TOOLTIPS[overallStatus];
  return (
    <span
      className={`esign-status-icon esign-status-icon--${overallStatus}`}
      aria-label={label}
      title={label}
      role="img"
    >
      {ICONS[overallStatus]}
    </span>
  );
};

export default EsignStatusIcon;
